import type {
  BlindSpotType,
  PanelDecisiveSignal,
} from "@shared/labs/level-assessment-interview-panel";
import {
  AlertTriangle,
  ArrowUpToLine,
  ChevronDown,
  EyeOff,
  FileText,
  HelpCircle,
  Link2,
  Linkedin,
  MessageSquare,
  Text,
  TrendingUp,
} from "lucide-react";
import type { ReactNode } from "react";
import { StrengthBars } from "../../results/critical-signals";
import { C, DM, MONO, UI } from "../../results/ui-tokens";
import type { StatusWord } from "./derive";

/**
 * Presentational primitives for the interview-panel folder — the pieces the
 * approved monolith uses that aren't already in the shared LA kit. Everything is
 * inline-styled against the `C/UI/DM/MONO` tokens (the LA idiom); Material
 * Symbols from the monolith are mapped to lucide here. Reuses `StrengthBars`
 * (the shared 3-bar meter) and, elsewhere, `LevelWhisker`.
 */

export const VIOLET = "#910048";
export const VIOLET_BG = "rgba(145,0,72,0.10)";
export const VIOLET_BD = "rgba(145,0,72,0.18)";

// ── Section scaffolding ──────────────────────────────────────────────────────

/** The uppercase eyebrow + optional inline caption above every section. */
export function SectionEyebrow({
  children,
  caption,
  color = C.ink50,
}: {
  children: ReactNode;
  caption?: string;
  color?: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
      <span
        style={{
          ...UI,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0",
          textTransform: "uppercase",
          color,
        }}
      >
        {children}
      </span>
      {caption && <span style={{ ...UI, fontSize: 13, color: C.ink50 }}>{caption}</span>}
    </div>
  );
}

/** One act of the panel's narrative — the page's top-level segmentation.
 *  Number + short title over a full-width divider, deliberately
 *  heavier than the small uppercase eyebrows that label blocks INSIDE an act,
 *  so the three-act flow reads at a glance. No captions — the content
 *  explains itself. */
export function ActHeader({ n, title }: { n: number; title: string }) {
  return (
    <div style={{ marginTop: 46, paddingTop: 26, borderTop: `1px solid ${C.ink10}` }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 11 }}>
        <span style={{ ...MONO, fontSize: 15, fontWeight: 700, color: C.red }}>{n}</span>
        <h2
          style={{
            ...UI,
            fontSize: 17.5,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: C.ink,
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>
    </div>
  );
}

/** A lifecycle statement as a plain colored word — Word-Carries-State: the
 *  text in its canonical tone is the whole status, no pill/dot/icon. */
export function StatusWordText({ word, size = 12.5 }: { word: StatusWord; size?: number }) {
  const color = word.tone === "green" ? C.green : word.tone === "amber" ? C.amberFg : C.ink50;
  return <span style={{ ...UI, fontSize: size, fontWeight: 600, color }}>{word.text}</span>;
}

// ── Evidence chips ───────────────────────────────────────────────────────────

const PACKET_ICON: Record<string, typeof FileText> = {
  linkedin: Linkedin,
  cv: FileText,
  interview_transcript: MessageSquare,
  free_text: Text,
};

/** One accumulated evidence packet. Unscored text packets (not yet in the
 *  score) get the violet "pending fusion" tint — the same motif as the timeline. */
export function EvidenceChip({
  type,
  label,
  roundLabel,
  scored,
}: {
  type: string;
  label: string;
  roundLabel: string;
  scored: boolean;
}) {
  const Icon = PACKET_ICON[type] ?? FileText;
  const violet = !scored;
  return (
    <span
      style={{
        ...DM,
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        fontSize: 13,
        fontWeight: 600,
        color: violet ? VIOLET : C.ink,
        background: violet ? VIOLET_BG : C.white,
        border: `1px solid ${violet ? VIOLET_BD : C.border}`,
        borderRadius: 999,
        padding: "6px 13px",
      }}
    >
      <Icon size={14} />
      {label}
      <span style={{ ...MONO, fontSize: 11.5, color: violet ? VIOLET : C.ink50, fontWeight: 400 }}>
        · {roundLabel}
      </span>
    </span>
  );
}

// ── Blind-spot badges + group captions ───────────────────────────────────────

interface BadgeSpec {
  icon: typeof EyeOff;
  /** Action-implying label: what the interviewer should DO about this gap. */
  label: string;
  /** Diagnostic gloss (tooltip): what the gap type means. */
  tooltip: string;
  tone: "neutral" | "amber";
}

const BLIND_SPOT_BADGE: Record<BlindSpotType, BadgeSpec> = {
  no_signal: {
    icon: EyeOff,
    label: "Estabelecer base",
    tooltip:
      "Sem sinal observável no material: a entrevista é o único canal para esta competência.",
    tone: "neutral",
  },
  abstention: {
    icon: EyeOff,
    label: "Destravar leitura",
    tooltip: "Evidência inconclusiva: o modelo não conseguiu inferir um nível.",
    tone: "amber",
  },
  low_confidence: {
    icon: AlertTriangle,
    label: "Confirmar nível",
    tooltip:
      "Baixa confiança: o nível apoia-se em evidência fraca. Um exemplo concreto confirma ou revisa.",
    tone: "amber",
  },
  out_of_band: {
    icon: ArrowUpToLine,
    label: "Verificar faixa",
    tooltip: "Nível fora da faixa esperada da disciplina: confirme antes de aceitar.",
    tone: "amber",
  },
  irregular: {
    icon: TrendingUp,
    label: "Explicar dispersão",
    tooltip: "As competências divergem muito entre si: entenda o porquê do perfil irregular.",
    tone: "amber",
  },
  unconfirmed_ceiling: {
    icon: ArrowUpToLine,
    label: "Testar teto",
    tooltip: "O nível pode ser um degrau acima: teste o limite superior.",
    tone: "amber",
  },
  // Ledger-sourced (multi-round fusion, v4 — docs/labs/level-assessment/multi-round-update-model.md §9).
  single_source: {
    icon: Link2,
    label: "Corroborar fonte",
    tooltip: "Leitura apoiada numa única fonte: corrobore em entrevista.",
    tone: "neutral",
  },
  follow_up: {
    icon: MessageSquare,
    label: "Retomar",
    tooltip: "Ponto levantado em rodada anterior e ainda aberto: retome de onde parou.",
    tone: "amber",
  },
  divergence: {
    icon: HelpCircle,
    label: "Esclarecer",
    tooltip: "O texto sugere algo que a avaliação ainda não reflete: esclareça na entrevista.",
    tone: "amber",
  },
};

/** The gap chip on an open point: the label says what to DO; the tooltip says
 *  what the gap means. */
export function BlindSpotBadge({ type }: { type: BlindSpotType }) {
  const spec = BLIND_SPOT_BADGE[type] ?? BLIND_SPOT_BADGE.abstention;
  const Icon = spec.icon;
  const fg = spec.tone === "amber" ? C.amberFg : C.ink70;
  const bg = spec.tone === "amber" ? C.amberBg : C.ink5;
  return (
    <span
      title={spec.tooltip}
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11.5,
        fontWeight: 600,
        color: fg,
        background: bg,
        borderRadius: 999,
        padding: "3px 9px",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={12} />
      {spec.label}
    </span>
  );
}

// ── Decisive-signal box (inside a known-read card) ───────────────────────────

/** True for a signal materialized from an interview claim rather than a
 *  LinkedIn-structured signal (design §2 S1: `key: "claim:<id>"`, `kind:
 *  "text"`). Absent `key`/`kind` (every signal materialized before the
 *  narrative layer shipped) → false, no chip. */
function isInterviewCitation(signal: PanelDecisiveSignal): boolean {
  return signal.kind === "text" && !!signal.key?.startsWith("claim:");
}

/** The small chip that tells an interview-sourced decisive signal apart from
 *  a LinkedIn-structured one — shows the signal's own humanized `value`
 *  (already composed server-side as e.g. "Entrevista (transcrição) · Rodada
 *  2"). */
function SourceTypeChip({ label }: { label: string }) {
  return (
    <span
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        color: VIOLET,
        background: VIOLET_BG,
        border: `1px solid ${VIOLET_BD}`,
        borderRadius: 999,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      <MessageSquare size={11} /> {label}
    </span>
  );
}

/** One decisive signal restated for "O que já sabemos": name + value + the
 *  shared strength meter + rubric tie-in + an optional "Ver citação" expander. */
export function DecisiveSignalBox({ signal }: { signal: PanelDecisiveSignal }) {
  const cite = signal.citation;
  const hasCite = !!cite && (cite.role || cite.meta || cite.quote);
  const interviewSourced = isInterviewCitation(signal);
  return (
    <div
      style={{
        background: C.paper,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 12,
        padding: "11px 13px",
        marginTop: 12,
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}
      >
        <span style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink }}>
          {signal.title}
          {signal.value && !interviewSourced && (
            <span style={{ color: C.ink70, fontWeight: 400 }}> · {signal.value}</span>
          )}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
          {interviewSourced && signal.value && <SourceTypeChip label={signal.value} />}
          <StrengthBars strength={signal.strength} />
        </span>
      </div>
      {signal.rubricConnection && (
        <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 6, lineHeight: 1.5 }}>
          <span style={{ ...MONO, fontSize: 11 }}>Rubrica:</span> {signal.rubricConnection}
        </div>
      )}
      {hasCite && (
        <details style={{ marginTop: 8 }}>
          <summary
            style={{
              ...UI,
              fontSize: 12.5,
              fontWeight: 500,
              color: C.red,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              listStyle: "none",
            }}
          >
            <ChevronDown size={13} className="chev" /> Ver citação
          </summary>
          <div style={{ marginTop: 8 }}>
            {(cite?.role || cite?.meta) && (
              <div style={{ ...MONO, fontSize: 11, color: C.ink50, marginBottom: 6 }}>
                {[cite?.role, cite?.meta].filter(Boolean).join(" · ")}
              </div>
            )}
            {cite?.quote && (
              <blockquote
                style={{
                  ...UI,
                  margin: 0,
                  fontSize: 13,
                  color: C.ink70,
                  lineHeight: 1.55,
                  background: C.white,
                  borderLeft: `3px solid ${C.red}`,
                  borderRadius: "0 8px 8px 0",
                  padding: "8px 12px",
                }}
              >
                "{cite.quote}"
              </blockquote>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

/** The "Sinal indireto — confirmar" pill for a proxy-only read. */
export function IndirectSignalPill() {
  return (
    <span
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        color: C.ink50,
        background: C.ink5,
        borderRadius: 999,
        padding: "5px 11px",
        marginTop: 12,
      }}
    >
      <Link2 size={13} /> Sinal indireto — confirmar na entrevista
    </span>
  );
}
